import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { motion } from "framer-motion";
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, ReferenceLine,
} from "recharts";
import { Droplet, TrendingUp, TrendingDown, AlertTriangle, Plus, Trash2, Scale, Info } from "lucide-react";
import WeightProtocolBanner from "@/components/WeightProtocolBanner";
import { useTelemetry } from "@/lib/telemetry";

const KG_TO_LB = 2.20462;

function kgToLb(kg) { return kg * KG_TO_LB; }
function lbToKg(lb) { return lb / KG_TO_LB; }

function fmt(value, unit) {
  if (value === null || value === undefined || Number.isNaN(value)) return "—";
  return `${Number(value).toFixed(1)} ${unit}`;
}

/**
 * Daily morning dry-weight tracker with fluid-overload alerts.
 * Threshold: > 1.36 kg / 3 lb overnight gain → red alert (potential fluid retention).
 * >  ~0.9 kg / 2 lb overnight → yellow watch.
 */
export default function WeightTracker() {
  useTelemetry("weight-fluid", "open");
  const [readings, setReadings] = useState([]);
  const [latestAlert, setLatestAlert] = useState(null);
  const [unit, setUnit] = useState("kg"); // 'kg' | 'lb'
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [weightInput, setWeightInput] = useState("");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [lastResponse, setLastResponse] = useState(null); // most recent POST response with alert flag

  const load = async () => {
    const { data } = await api.get("/tracker/weight");
    setReadings(data.readings || []);
    setLatestAlert(data.latest_alert || null);
  };

  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    const v = parseFloat(weightInput);
    if (!v || v <= 0) return toast.error("Enter a positive weight");
    const kg = unit === "kg" ? v : lbToKg(v);
    setSaving(true);
    try {
      const { data } = await api.post("/tracker/weight", {
        date, weight_kg: parseFloat(kg.toFixed(2)),
        note: note.trim() || null,
        source: "patient_reported",
      });
      setLastResponse(data);
      if (data.alert?.level === "high") toast.error(data.alert.message, { duration: 8000 });
      else if (data.alert?.level === "medium") toast.warning(data.alert.message);
      else toast.success(`Weight ${fmt(kg, "kg")} logged`);
      setWeightInput(""); setNote("");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save weight");
    } finally { setSaving(false); }
  };

  const removeReading = async (id) => {
    await api.delete(`/tracker/weight/${id}`);
    toast.success("Removed");
    await load();
  };

  // Build a delta list per reading for the table
  const rows = useMemo(() => {
    const list = [...readings].sort((a, b) => (a.date < b.date ? -1 : 1));
    return list.map((r, i) => {
      const prev = i > 0 ? list[i - 1] : null;
      const delta = prev ? +(r.weight_kg - prev.weight_kg).toFixed(2) : null;
      return { ...r, delta };
    }).reverse();
  }, [readings]);

  const chartData = useMemo(() => {
    return [...readings]
      .sort((a, b) => (a.date < b.date ? -1 : 1))
      .slice(-30)
      .map((r) => ({ date: r.date.slice(5), value: unit === "kg" ? r.weight_kg : +kgToLb(r.weight_kg).toFixed(1) }));
  }, [readings, unit]);

  const latest = readings.length ? readings[readings.length - 1] : null;
  const alertFlag = lastResponse?.alert || latestAlert?.flag || null;
  const trend7 = useMemo(() => {
    if (readings.length < 2) return null;
    const first = readings[Math.max(0, readings.length - 7)];
    const last = readings[readings.length - 1];
    const delta = last.weight_kg - first.weight_kg;
    return { delta, since: first.date };
  }, [readings]);

  return (
    <div className="space-y-8" data-testid="weight-tracker-page">
      <header className="border-b border-[#272E2B] pb-4">
        <div className="flex items-center gap-3">
          <Droplet size={26} className="text-[#60A5FA]" />
          <h2 className="font-heading text-2xl">Dry Weight &amp; Fluid</h2>
        </div>
        <p className="text-sm text-[#A3ACA7] mt-2 max-w-3xl">
          Weigh yourself every morning after using the bathroom, before eating or drinking.
          A jump of more than 3 lb (1.36 kg) overnight can be an early sign of fluid retention —
          Med Nexus will warn you and quietly notify your care team.
        </p>
      </header>

      {/* Per-specialty clinical protocol reference (Phase 3b) */}
      <WeightProtocolBanner />

      {/* Alert banner (persistent while unresolved) */}
      {alertFlag && (
        <motion.div
          initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }}
          className={`ros-card border ${alertFlag.level === "high"
            ? "!border-[#F87171]/60 !bg-[#F87171]/10"
            : "!border-[#FBBF24]/40 !bg-[#FBBF24]/10"}`}
          data-testid={`weight-alert-${alertFlag.level}`}
        >
          <div className="flex items-start gap-3">
            <AlertTriangle size={22}
              className={alertFlag.level === "high" ? "text-[#F87171] shrink-0 mt-0.5" : "text-[#FBBF24] shrink-0 mt-0.5"} />
            <div>
              <div className="font-heading text-lg text-white">
                {alertFlag.level === "high" ? "Possible fluid overload" : "Weight change worth watching"}
              </div>
              <div className="text-sm text-white/80 mt-1">{alertFlag.message}</div>
              {alertFlag.level === "high" && (
                <div className="text-xs text-[#A3ACA7] mt-2">
                  Check for swollen ankles, tight rings, puffiness around the eyes, or shortness of breath. Contact your nephrology team same-day if any of these are present.
                </div>
              )}
            </div>
          </div>
        </motion.div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Entry panel */}
        <div className="ros-card md:col-span-1 h-fit" data-testid="weight-entry-card">
          <div className="flex items-center justify-between">
            <h3 className="font-heading text-lg text-white">Log this morning&apos;s weight</h3>
            <div className="flex items-center gap-1 text-xs bg-[#1C201E] border border-[#272E2B] rounded-full p-1">
              {["kg", "lb"].map((u) => (
                <button
                  key={u}
                  data-testid={`weight-unit-${u}`}
                  onClick={() => setUnit(u)}
                  className={`px-2 py-0.5 rounded-full transition-colors ${
                    unit === u ? "bg-[#4ADE80] text-black font-semibold" : "text-[#A3ACA7]"
                  }`}
                >{u}</button>
              ))}
            </div>
          </div>

          <form onSubmit={submit} className="space-y-4 mt-5">
            <div>
              <label className="block text-xs uppercase tracking-wider text-[#A3ACA7] mb-1">Date</label>
              <input
                type="date"
                value={date}
                data-testid="weight-date"
                onChange={(e) => setDate(e.target.value)}
                className="w-full bg-[#1C201E] border border-[#272E2B] rounded-lg p-2.5 text-white focus:outline-none focus:border-[#60A5FA]"
              />
            </div>
            <div>
              <label className="block text-xs uppercase tracking-wider text-[#A3ACA7] mb-1">Weight ({unit})</label>
              <input
                type="number"
                step="0.1"
                min="10"
                value={weightInput}
                data-testid="weight-value"
                onChange={(e) => setWeightInput(e.target.value)}
                placeholder={unit === "kg" ? "e.g. 78.4" : "e.g. 172.8"}
                className="w-full bg-[#1C201E] border border-[#272E2B] rounded-lg p-2.5 text-white focus:outline-none focus:border-[#60A5FA]"
              />
            </div>
            <div>
              <label className="block text-xs uppercase tracking-wider text-[#A3ACA7] mb-1">Note (optional)</label>
              <input
                type="text"
                value={note}
                data-testid="weight-note"
                onChange={(e) => setNote(e.target.value)}
                placeholder="e.g. felt puffy"
                className="w-full bg-[#1C201E] border border-[#272E2B] rounded-lg p-2.5 text-white focus:outline-none focus:border-[#60A5FA]"
              />
            </div>
            <button
              type="submit"
              disabled={saving}
              data-testid="weight-submit"
              className="w-full bg-[#60A5FA]/10 text-[#60A5FA] border border-[#60A5FA]/40 hover:bg-[#60A5FA] hover:text-black transition-colors rounded-full py-2.5 font-semibold flex items-center justify-center gap-2 disabled:opacity-60"
            >
              <Plus size={16} /> {saving ? "Saving…" : "Log weight"}
            </button>
          </form>

          <div className="mt-6 pt-6 border-t border-[#272E2B] space-y-3 text-xs text-[#A3ACA7]">
            <div className="flex items-start gap-2">
              <Info size={14} className="text-[#60A5FA] shrink-0 mt-0.5" />
              <div>The system warns you above <span className="text-white font-semibold">+3 lb (1.36 kg)</span> overnight — a KDIGO signal for possible fluid retention in CKD/HF.</div>
            </div>
          </div>
        </div>

        {/* Chart + summary */}
        <div className="ros-card md:col-span-2" data-testid="weight-chart-card">
          <div className="flex items-center justify-between">
            <div>
              <div className="ros-label">Weight trend · last 30 days</div>
              <div className="text-xs text-[#A3ACA7] mt-1">
                {latest ? `Most recent: ${fmt(unit === "kg" ? latest.weight_kg : kgToLb(latest.weight_kg), unit)} on ${latest.date}` : "No readings yet"}
              </div>
            </div>
            {trend7 && (
              <div className={`px-3 py-1.5 rounded-full text-xs flex items-center gap-1.5 border ${
                trend7.delta > 0.5 ? "text-[#F87171] border-[#F87171]/40 bg-[#F87171]/10"
                  : trend7.delta < -0.5 ? "text-[#60A5FA] border-[#60A5FA]/40 bg-[#60A5FA]/10"
                  : "text-[#A3ACA7] border-[#272E2B]"}`}>
                {trend7.delta >= 0 ? <TrendingUp size={12}/> : <TrendingDown size={12}/>}
                {trend7.delta >= 0 ? "+" : ""}{(unit === "kg" ? trend7.delta : kgToLb(trend7.delta)).toFixed(1)} {unit} vs {trend7.since}
              </div>
            )}
          </div>

          <div className="mt-6 h-[240px]">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData} margin={{ top: 10, right: 12, bottom: 0, left: -20 }}>
                <CartesianGrid stroke="#1C201E" strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="date" stroke="#A3ACA7" fontSize={11} tickLine={false} axisLine={false} />
                <YAxis stroke="#A3ACA7" fontSize={11} tickLine={false} axisLine={false} width={44}
                       {...(() => {
                         const vs = chartData.map((d) => d.value).filter((v) => !Number.isNaN(v));
                         if (!vs.length) return { domain: [0, 10] };
                         const lo = Math.floor(Math.min(...vs) - 1);
                         const hi = Math.ceil(Math.max(...vs) + 1);
                         const step = Math.ceil((hi - lo) / 4);
                         const top = lo + step * 4;
                         return {
                           domain: [lo, top],
                           ticks: [lo, lo + step, lo + 2 * step, lo + 3 * step, top],
                         };
                       })()}
                       allowDecimals={false}
                       tickFormatter={(v) => Math.round(v).toString()} />
                <Tooltip
                  contentStyle={{ background: "#121514", border: "1px solid #272E2B", borderRadius: 12, color: "#fff" }}
                  labelStyle={{ color: "#A3ACA7", fontSize: 11 }}
                  formatter={(v) => [`${v} ${unit}`, "Weight"]}
                />
                {readings.length > 1 && (() => {
                  // draw a "fluid alert" reference line at (previous weight + 1.36 kg)
                  const prev = readings[readings.length - 2];
                  const y = prev && (unit === "kg" ? prev.weight_kg + 1.36 : kgToLb(prev.weight_kg + 1.36));
                  return y ? (
                    <ReferenceLine y={+y.toFixed(1)} stroke="#F87171" strokeDasharray="4 4"
                                   label={{ value: "Alert threshold (+3 lb)", position: "insideTopRight", fill: "#F87171", fontSize: 10 }} />
                  ) : null;
                })()}
                <Line type="monotone" dataKey="value" stroke="#60A5FA" strokeWidth={3}
                      dot={{ r: 3, fill: "#60A5FA" }} activeDot={{ r: 5 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* History table */}
      <div className="ros-card !p-0 overflow-hidden" data-testid="weight-history-card">
        <div className="p-6 border-b border-[#272E2B] bg-[#171c19] flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Scale size={16} className="text-[#60A5FA]" />
            <h3 className="font-heading text-lg text-white">History</h3>
          </div>
          <div className="text-xs text-[#A3ACA7]">{readings.length} readings</div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-[#0f1311] text-[#A3ACA7] text-xs uppercase tracking-wider">
                <th className="p-4 font-medium">Date</th>
                <th className="p-4 font-medium">Weight</th>
                <th className="p-4 font-medium">Change</th>
                <th className="p-4 font-medium">Note</th>
                <th className="p-4"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#272E2B]">
              {rows.length === 0 && (
                <tr><td colSpan={5} className="p-8 text-center text-[#A3ACA7]">No weights logged yet.</td></tr>
              )}
              {rows.map((r) => {
                const displayWeight = unit === "kg" ? r.weight_kg : kgToLb(r.weight_kg);
                const dKg = r.delta;
                const dDisp = dKg == null ? null : (unit === "kg" ? dKg : dKg * KG_TO_LB);
                const isBigJump = dKg != null && dKg >= 1.36;
                const isWatch = dKg != null && dKg >= 0.9 && dKg < 1.36;
                return (
                  <tr key={r.id} className="hover:bg-[#1a1f1c] transition-colors" data-testid={`weight-row-${r.date}`}>
                    <td className="p-4 text-white/90 font-medium">{r.date}</td>
                    <td className="p-4 ros-metric text-lg text-white">{displayWeight.toFixed(1)} <span className="text-xs text-[#A3ACA7]">{unit}</span></td>
                    <td className="p-4">
                      {dDisp == null ? (
                        <span className="text-[#3f4a44]">—</span>
                      ) : (
                        <span
                          data-testid={`weight-delta-${r.date}`}
                          className={`inline-flex items-center gap-1 text-sm font-semibold ${
                            isBigJump ? "text-[#F87171]"
                            : isWatch ? "text-[#FBBF24]"
                            : dKg > 0 ? "text-white/80"
                            : "text-[#60A5FA]"
                          }`}
                        >
                          {dDisp >= 0 ? <TrendingUp size={14}/> : <TrendingDown size={14}/>}
                          {dDisp >= 0 ? "+" : ""}{dDisp.toFixed(1)} {unit}
                          {isBigJump && <AlertTriangle size={12} className="ml-1" />}
                        </span>
                      )}
                    </td>
                    <td className="p-4 text-sm text-[#A3ACA7]">{r.note || "—"}</td>
                    <td className="p-4 text-right">
                      <button
                        onClick={() => removeReading(r.id)}
                        className="text-[#A3ACA7] hover:text-red-400 p-1"
                        data-testid={`weight-delete-${r.id}`}
                        title="Delete reading"
                      >
                        <Trash2 size={14} />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
